import { Button, Checkbox, Flex, Text } from '@modusign/design-system';
import PropTypes from 'prop-types';
import PropsType from 'props-type';
import { ChangeEvent, useState } from 'react';
import { useDispatch } from 'react-redux';

import useCancelBulkRequest from '@app/features/bulkRequest/hooks/useCancelBulkRequest';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { modalClose } from '@app/store/modal/actions';

import './BulkRequestCancelModal.scss';

const propTypes = {
  bulkRequestId: PropTypes.string.isRequired,
};

type BulkRequestCancelModalProps = PropsType<typeof propTypes>;

function BulkRequestCancelModal({ bulkRequestId }: BulkRequestCancelModalProps) {
  const [blockSignerAccess, setBlockSignerAccess] = useState(false);
  const [message, setMessage] = useState('');
  const dispatch = useDispatch();

  const { createErrorHandler } = useOpenErrorModal();
  const workspaceId = useWorkspaceId();
  const { mutateAsync: cancelBulkRequest, isLoading } = useCancelBulkRequest(bulkRequestId);

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const { value } = event.target;
    setMessage(value);
  }

  return (
    <div className="Container Container--max-xs">
      <div className="Card Card--pop box-7x align-center">
        <h3 className="space-4x">
          <strong>서명 요청 취소하기</strong>
        </h3>
        <p className="space-3x">
          정말 서명 요청을 취소하시겠습니까?
          <br />
          서명 요청을 취소할 경우 더이상
          <br />
          서명이 진행되지 않습니다.
        </p>
        <p className="space-3x">* 이미 모든 서명이 완료된 문서는 취소되지 않습니다.</p>
        <div className="InputSet">
          <textarea
            className="InputSet__text-area BulkRequestCancelModal__textarea"
            cols={30}
            maxLength={200}
            name="textArea"
            placeholder="취소 사유를 입력해주세요. 입력한 내용은 서명 요청자 및 모든 서명자에게 전송됩니다."
            rows={6}
            value={message}
            onChange={handleChange}
          />
          <p className="align-right">
            {message.length}
            /200
          </p>
        </div>
        <div className="space-2x">
          <Flex
            as="button"
            backgroundColor="buttonBackgroundNeutralLight"
            border="1px solid"
            borderColor="#EEEEEE"
            borderRadius="3px"
            boxShadow="0 3px 2px -2px $color-white;"
            mb={4}
            px={4}
            py="10px"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                borderColor: '#DEDEDE',
                boxShadow: '0 3px 2px -2px $color-black-opacity06',
              },
            }}
            width="100%"
            onClick={() => {
              setBlockSignerAccess(!blockSignerAccess);
            }}
          >
            <Checkbox checked={blockSignerAccess} mr={2} mt="2px" weight="md" />
            <Text
              mb={0}
              ml={2}
              sx={{
                cursor: 'pointer',
                fontSize: 'sm',
                fontWeight: 'medium',
                wordBreak: 'normal',
              }}
              textAlign="left"
            >
              취소한 문서는 요청자 님을 제외한 모든 서명자가 볼 수 없도록 설정합니다.
            </Text>
          </Flex>
        </div>

        <button
          className="Btn"
          type="button"
          onClick={() => {
            dispatch(modalClose());
          }}
        >
          닫기
        </button>
        <Button
          disabled={message.length < 2}
          isLoading={isLoading}
          variant="primary"
          onClick={() =>
            cancelBulkRequest([workspaceId, bulkRequestId, { blockSignerAccess, message }], {
              onSuccess: () => {
                dispatch(modalClose());
              },
              onError: createErrorHandler({ title: '대량전송 취소 실패' }),
            })
          }
        >
          요청 취소하기
        </Button>
      </div>
    </div>
  );
}

BulkRequestCancelModal.propTypes = propTypes;

export default BulkRequestCancelModal;
